import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSubscriptionWarning } from '@/hooks/useSubscriptionWarning';
import { getSubscriptionWarningCopy, HARDSHIP_EMAIL } from '@/lib/subscriptionWarningCopy';
import { useOrgStore } from '@/stores/orgStore';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';

/**
 * The interrupting half of the escalation. A banner carries the fortnight's notice; this
 * appears in the final week and again every session once access has lapsed.
 *
 * Only owners and managers see it. An employee cannot act on a subscription, and telling
 * them their employer has not paid is not the platform's message to deliver.
 */
export function SubscriptionWarningDialog() {
  const organization = useOrgStore((state) => state.organization);
  const memberRole = useOrgStore((state) => state.memberRole);
  const warning = useSubscriptionWarning();
  const [dismissed, setDismissed] = useState(false);

  const level = warning.data?.level ?? 'NONE';
  const shouldInterrupt = level === 'FINAL_WEEK' || level === 'LAPSED';
  const canAct = memberRole === 'BO' || memberRole === 'BM';
  const storageKey =
    organization?.id && warning.data
      ? `subscription-warning:${organization.id}:${level}:${warning.data.deadline ?? 'none'}`
      : null;

  useEffect(() => {
    if (!storageKey) return;
    try {
      setDismissed(window.sessionStorage.getItem(storageKey) === 'dismissed');
    } catch {
      // Private browsing can refuse session storage. Showing the warning again is the
      // safe side of that failure.
      setDismissed(false);
    }
  }, [storageKey]);

  if (!canAct || !shouldInterrupt || dismissed) return null;

  const copy = warning.data ? getSubscriptionWarningCopy(warning.data) : null;
  if (!copy) return null;

  const close = () => {
    setDismissed(true);
    if (!storageKey) return;
    try {
      window.sessionStorage.setItem(storageKey, 'dismissed');
    } catch {
      // Nothing to do: the warning simply returns on the next render.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WorklynxIcon name="warning" size={20} className="text-warning" />
            {copy.title}
          </DialogTitle>
          <DialogDescription>{organization?.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-5">
          <p className="text-sm text-foreground">{copy.body}</p>

          {copy.hardship && (
            <p className="border-brand-border/60 bg-surface rounded-md border p-3 text-sm text-muted">
              {copy.hardship.split(HARDSHIP_EMAIL).flatMap((part, index, parts) =>
                index < parts.length - 1
                  ? [
                      part,
                      <a
                        key={HARDSHIP_EMAIL}
                        href={`mailto:${HARDSHIP_EMAIL}`}
                        className="font-medium text-brand underline"
                      >
                        {HARDSHIP_EMAIL}
                      </a>,
                    ]
                  : [part],
              )}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {memberRole === 'BO' && (
              <Button asChild onClick={close}>
                <Link to="/bo/billing">
                  <WorklynxIcon name="billing" size={16} />
                  {copy.actionLabel}
                </Link>
              </Button>
            )}
            <Button type="button" variant="outline" onClick={close}>
              {level === 'LAPSED' ? 'Continue for now' : 'Remind me later'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
